import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { numberID } from "@/lib/format";
import { wibToday } from "@/lib/bizdate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PackageMinus } from "lucide-react";

export default function PemakaianInternal() {
  const { can, user } = useAuth();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ departments: [] });
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const canIssue = can("inventory.general.issue");

  const load = () => { setRows(null); api.get("/general-inventory/internal-issues").then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); }); };
  useEffect(() => {
    load();
    api.get("/general-inventory/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/general-inventory/items", { params: { status: "active" } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader title="Pemakaian Internal" subtitle="Pengeluaran General Supplies untuk keperluan internal perusahaan. Mengurangi stok & dicatat auditable (tanpa penjualan)." testId="internal-issue-header">
        {canIssue && <Button onClick={() => setOpen(true)} data-testid="add-internal-issue-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Pemakaian Baru</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" data-testid="internal-issue-table">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada pemakaian internal" icon={PackageMinus} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead><TableHead>Tanggal</TableHead><TableHead>Item</TableHead>
              <TableHead className="text-right">Qty</TableHead><TableHead>Departemen</TableHead><TableHead>Keperluan</TableHead>
              <TableHead>Biaya</TableHead><TableHead>Oleh</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`internal-issue-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.iss_no}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.date}</TableCell>
                  <TableCell className="font-medium">{r.item_name}</TableCell>
                  <TableCell className="text-right text-rose-600">-{numberID(r.qty)} {r.unit}</TableCell>
                  <TableCell className="text-xs">{r.department || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.purpose || "-"}</TableCell>
                  <TableCell className="text-xs">{r.cost_available ? `Rp${numberID(r.total_cost)}` : <span className="text-slate-400">Cost belum tersedia</span>}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.created_by_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {open && <IssueForm items={items} meta={meta} defaultRequester={user?.name} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); api.get("/general-inventory/items", { params: { status: "active" } }).then((r) => setItems(r.data)); }} />}
    </div>
  );
}

function IssueForm({ items, meta, defaultRequester, onClose, onSaved }) {
  const [f, setF] = useState({ date: wibToday(), item_id: "", qty: "", department: "", purpose: "", requester: defaultRequester || "", notes: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const sel = items.find((i) => i.id === f.item_id);
  const save = async () => {
    if (!f.item_id) return toast.error("Pilih item");
    if (!f.qty || Number(f.qty) <= 0) return toast.error("Qty harus lebih dari 0");
    try {
      await api.post("/general-inventory/internal-issue", { ...f, qty: Number(f.qty) });
      toast.success("Pemakaian internal dicatat, stok diperbarui");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="internal-issue-dialog">
        <DialogHeader><DialogTitle>Pemakaian Internal Baru</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Tanggal (WIB)</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="ii-f-date" /></div>
          <div>
            <Label>Item *</Label>
            <Select value={f.item_id} onValueChange={(v) => set("item_id", v)}>
              <SelectTrigger data-testid="ii-f-item"><SelectValue placeholder="Pilih item" /></SelectTrigger>
              <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({numberID(i.on_hand)} {i.unit})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Qty * {sel ? `(${sel.unit})` : ""}</Label><Input type="number" value={f.qty} onChange={(e) => set("qty", e.target.value)} data-testid="ii-f-qty" />{sel && <div className="mt-1 text-xs text-slate-400">Tersedia: {numberID(sel.available)} {sel.unit}</div>}</div>
          <div>
            <Label>Departemen</Label>
            <Select value={f.department || "NONE"} onValueChange={(v) => set("department", v === "NONE" ? "" : v)}>
              <SelectTrigger data-testid="ii-f-dept"><SelectValue placeholder="Pilih departemen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">(Tidak ditentukan)</SelectItem>
                {meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Keperluan / Purpose</Label><Input value={f.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="ii-f-purpose" /></div>
          <div className="col-span-2"><Label>Requester</Label><Input value={f.requester} onChange={(e) => set("requester", e.target.value)} data-testid="ii-f-requester" /></div>
          <div className="col-span-2"><Label>Catatan</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="ii-f-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-500" data-testid="ii-f-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
